import { givenName, type AddressAs } from './assistant';
import { businessDayOf } from './assistant-briefing';
import type { MemoryKind, MemoryScope, MemorySource } from './assistant-proactive';
import type { Id } from './common';

// Hana's memory (S3 §9, L4 D163–D197): the DTOs, the bodies, the limits and the strings the API and the web share.
// Pure: no I/O, so the memory page, the "Em đã nhớ" line and the server render the same labels.

/** Active personal notes per person (rejected markers excluded) and company notes (active + proposed), D173 ★. */
export const MEMORY_PERSONAL_LIMIT = 200;
export const MEMORY_COMPANY_LIMIT = 300;
/** A note's text, counting each object token as its label (D7, D169). */
export const MEMORY_TEXT_MAX = 300;
/** How long a claimed notice stays with the tab that claimed it (§9.7, D191). */
export const MEMORY_NOTICE_LEASE_MS = 60_000;

/** The object types a stored token may name: `{{task|note|project|user:<uuid>}}` (§5.1). */
export const MEMORY_TOKEN_TYPES = ['task', 'note', 'project', 'user'] as const;
export type MemoryTokenType = (typeof MEMORY_TOKEN_TYPES)[number];
/** The kinds a person's own note may take; `glossary` is company-only (§5.1). */
export const MEMORY_PERSONAL_KINDS = ['preference', 'habit', 'fact'] as const;
export type MemoryPersonalKind = (typeof MEMORY_PERSONAL_KINDS)[number];
/** The kinds a company note may take (§9.6). */
export const MEMORY_COMPANY_KINDS = ['fact', 'glossary'] as const;
export type MemoryCompanyKind = (typeof MEMORY_COMPANY_KINDS)[number];

/**
 * One piece of a note's text as the reader may see it now (D170): plain text, an object resolved under the reader's
 * current rights (`label` = its title or the user's name), or an object the reader no longer sees. A raw `{{…}}`
 * token never reaches the client.
 */
export type MemoryPart = { text: string } | { ref: { type: MemoryTokenType; id: Id; label: string } } | { hidden: true };

/**
 * One note (§9.7). `evidenceInDays`: the work-hours note, whose count is days, not times (D171). `originConversationId`
 * is set only while that conversation is still the reader's. `canPropose`: "Gợi ý cho công ty" may show (§9.6);
 * `proposalSubmitted`: it was sent already. Company rows carry `canPropose`/`proposalSubmitted` false.
 */
export type MemoryDTO = { id: Id; scope: MemoryScope; kind: MemoryKind; source: MemorySource; parts: MemoryPart[];
  pinned: boolean; evidenceCount: number; evidenceInDays: boolean; originConversationId: Id | null;
  createdAt: string; updatedAt: string; version: number; canPropose: boolean; proposalSubmitted: boolean };
/** One personal group of the page, in `MEMORY_PERSONAL_GROUPS` order. */
export type MemoryGroupDTO = { kind: MemoryPersonalKind; items: MemoryDTO[] };
/** `GET /api/assistant/memories`: the person's active notes grouped (no markers), then the active company notes,
 *  read-only. `count` = active personal notes, what the 200 limit counts. */
export type MemoryListDTO = { personal: MemoryGroupDTO[]; company: MemoryDTO[]; count: number };

/** `POST /api/assistant/memories` (told). `kind` absent = `preference`. */
export type MemoryCreateBody = { text: string; kind?: MemoryPersonalKind };
/** `PATCH /api/assistant/memories/:id` (D174): a text edit turns a learned note into a told one. */
export type MemoryPatchBody = { version: number; text?: string; pinned?: boolean };
/** `POST /api/assistant/memories/:id/propose-company` (D189). */
export type MemoryProposeBody = { version: number };
/** Its answer: never the company row's id or queue state. */
export type MemoryProposeDTO = { submitted: true };

/** One "Em đã nhớ" note as claimed (D191): the delivery token the ack and discard must carry back. */
export type MemoryNoticeDTO = { id: Id; memoryId: Id; memoryVersion: number; deliveryToken: Id; parts: MemoryPart[];
  canPropose: boolean };
/** `POST /api/assistant/memory-notices/claim` (at most 3). */
export type MemoryNoticeClaimBody = Record<string, never>;
export type MemoryNoticeClaimDTO = { notices: MemoryNoticeDTO[] };
/** `POST …/:id/ack` and `POST …/:id/discard`. */
export type MemoryNoticeAckBody = { deliveryToken: Id; memoryVersion: number };
export type MemoryNoticeDiscardBody = MemoryNoticeAckBody;
/** The answer of an ack or a discard: the notice's id, nothing else. */
export type MemoryNoticeDoneDTO = { id: Id };

// Company knowledge (§9.6, D190, D194). Personal notes never appear here, not even as counts.

/** An active company note on the admin tab. */
export type KnowledgeItemDTO = { id: Id; kind: MemoryCompanyKind; text: string; source: 'admin' | 'suggested';
  createdAt: string; updatedAt: string; version: number };
/** A proposal in "Hana đề xuất": text, kind and day only — nothing that leads back to the person. */
export type KnowledgeProposalDTO = { id: Id; kind: MemoryCompanyKind; text: string; createdAt: string };
export type KnowledgeListDTO = { proposals: KnowledgeProposalDTO[]; items: KnowledgeItemDTO[] };
export type KnowledgeCreateBody = { kind: MemoryCompanyKind; text: string };
export type KnowledgePatchBody = { version: number; kind?: MemoryCompanyKind; text?: string };

// Strings (D171–D173, D177, D193 ★).

/**
 * How a memory string names the person: the prompt's form (S1), so "anh", "chị", the given name or the custom form —
 * the mockup's "anh bảo", never "anh Bình bảo".
 */
export function memoryAddressOf(p: { addressAs: AddressAs; addressCustom: string | null }, name: string): string {
  switch (p.addressAs) {
    case 'anh': return 'anh';
    case 'chi': return 'chị';
    case 'name': return givenName(name);
    case 'custom': return p.addressCustom ?? givenName(name);
  }
}

/** The owner's own words carry their persona name (S8 D9); the rest say "trợ lý" (D39). */
export const memoryLimitMessage = (address: string, persona: string): string =>
  `${persona} đã nhớ ${MEMORY_PERSONAL_LIMIT} điều về ${address}. Xoá bớt rồi thêm lại nhé.`;
export const memoryCompanyLimitMessage = (): string => `Kiến thức công ty đã đủ ${MEMORY_COMPANY_LIMIT} mục.`;
/** D177 ★: told text refuses credentials and account numbers only. */
export const MEMORY_CREDENTIAL_MESSAGE = 'Trợ lý không nhớ mật khẩu hay số tài khoản.';
/** An object token the reader no longer sees (D170, mockup M:292). */
export const MEMORY_HIDDEN_TEXT = '(một mục không còn truy cập được)';
export const MEMORY_FROM_CONVERSATION = 'Từ hội thoại';
export const MEMORY_COMPANY_OFF_TEXT = 'Công ty đang tắt trí nhớ của trợ lý.';
export const memoryEmptyText = (address: string, persona: string): string => `${persona} chưa nhớ gì về ${address}.`;
export const memoryForgetAllTitle = (address: string, persona: string): string =>
  `Quên hết những gì ${persona} nhớ về ${address}?`;

const dayMonth = (instant: string): string => {
  const day = businessDayOf(instant);
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
};

/** D171 ★: "anh bảo · 21/09", "em tự học · 3 lần · 28/09", "em tự học · 42 ngày · 30/09"; null for company notes.
 *  The day is the note's last change, in Ho Chi Minh time. */
export function memorySourceLabel(
  m: Pick<MemoryDTO, 'scope' | 'source' | 'evidenceCount' | 'evidenceInDays' | 'updatedAt'>, address: string,
): string | null {
  if (m.scope !== 'personal') return null;
  const day = dayMonth(m.updatedAt);
  if (m.source === 'learned') return `em tự học · ${m.evidenceCount} ${m.evidenceInDays ? 'ngày' : 'lần'} · ${day}`;
  return `${address} bảo · ${day}`;
}

/** D172: the personal groups in mockup order. */
export const MEMORY_PERSONAL_GROUPS = MEMORY_PERSONAL_KINDS;
const PERSONAL_GROUP_LABELS: Record<MemoryPersonalKind, string> = {
  preference: 'Sở thích', habit: 'Thói quen', fact: 'Điều cần biết',
};
export const memoryGroupHeading = (kind: MemoryPersonalKind, n: number): string => `${PERSONAL_GROUP_LABELS[kind]} · ${n}`;
export const memoryCompanyHeading = (n: number): string => `Kiến thức công ty · chỉ đọc · ${n}`;

/** D172: the admin tab's groups — the proposals, then "Thuật ngữ" (glossary) and "Quy ước" (fact). */
export const KNOWLEDGE_GROUPS = ['proposed', 'glossary', 'fact'] as const;
export type KnowledgeGroup = (typeof KNOWLEDGE_GROUPS)[number];
const KNOWLEDGE_GROUP_LABELS: Record<KnowledgeGroup, string> = {
  proposed: 'Trợ lý đề xuất', glossary: 'Thuật ngữ', fact: 'Quy ước',
};
export const knowledgeGroupHeading = (group: KnowledgeGroup, n: number): string =>
  `${KNOWLEDGE_GROUP_LABELS[group]} · ${n}`;

/** Personal notes into their groups, in group order, keeping each group's item order; empty groups are left out. */
export function groupMemories<T extends { kind: MemoryKind }>(items: readonly T[]): { kind: MemoryPersonalKind; items: T[] }[] {
  return MEMORY_PERSONAL_GROUPS
    .map((kind) => ({ kind, items: items.filter((m) => m.kind === kind) }))
    .filter((g) => g.items.length > 0);
}
